/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  MutationKey,
  useMutation,
  UseMutationResult,
} from "@tanstack/react-query";
import { GramCore } from "../core.js";
import { accessSetDirectoryRoleMapping } from "../funcs/accessSetDirectoryRoleMapping.js";
import { combineSignals } from "../lib/primitives.js";
import { RequestOptions } from "../lib/sdks.js";
import { DirectoryRoleMapping } from "../models/components/directoryrolemapping.js";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  SetDirectoryRoleMappingRequest,
  SetDirectoryRoleMappingSecurity,
} from "../models/operations/setdirectoryrolemapping.js";
import { unwrapAsync } from "../types/fp.js";
import { useGramContext } from "./_context.js";
import { MutationHookOptions } from "./_types.js";

export type SetDirectoryRoleMappingMutationVariables = {
  request: SetDirectoryRoleMappingRequest;
  security?: SetDirectoryRoleMappingSecurity | undefined;
  options?: RequestOptions;
};

export type SetDirectoryRoleMappingMutationData = DirectoryRoleMapping;

export type SetDirectoryRoleMappingMutationError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * setDirectoryRoleMapping access
 *
 * @remarks
 * Map a directory group or attribute value to a role, replacing any role it was mapped to before.
 */
export function useSetDirectoryRoleMappingMutation(
  options?: MutationHookOptions<
    SetDirectoryRoleMappingMutationData,
    SetDirectoryRoleMappingMutationError,
    SetDirectoryRoleMappingMutationVariables
  >,
): UseMutationResult<
  SetDirectoryRoleMappingMutationData,
  SetDirectoryRoleMappingMutationError,
  SetDirectoryRoleMappingMutationVariables
> {
  const client = useGramContext();
  return useMutation({
    ...buildSetDirectoryRoleMappingMutation(client, options),
    ...options,
  });
}

export function mutationKeySetDirectoryRoleMapping(): MutationKey {
  return ["@gram/client", "access", "setDirectoryRoleMapping"];
}

export function buildSetDirectoryRoleMappingMutation(
  client$: GramCore,
  hookOptions?: RequestOptions,
): {
  mutationKey: MutationKey;
  mutationFn: (
    variables: SetDirectoryRoleMappingMutationVariables,
  ) => Promise<SetDirectoryRoleMappingMutationData>;
} {
  return {
    mutationKey: mutationKeySetDirectoryRoleMapping(),
    mutationFn: function setDirectoryRoleMappingMutationFn({
      request,
      security,
      options,
    }): Promise<SetDirectoryRoleMappingMutationData> {
      const mergedOptions = {
        ...hookOptions,
        ...options,
        fetchOptions: {
          ...hookOptions?.fetchOptions,
          ...options?.fetchOptions,
          signal: combineSignals(
            hookOptions?.fetchOptions?.signal,
            options?.fetchOptions?.signal,
          ),
        },
      };
      return unwrapAsync(accessSetDirectoryRoleMapping(
        client$,
        request,
        security,
        mergedOptions,
      ));
    },
  };
}
